import { restaurantApi } from '../config/apiClient';

// ============================================
// TYPES
// ============================================

export interface RestaurantScreenDto {
  Id: number;
  Name: string;
  BranchId?: string | null;
  QuickSaleScreenId: number;
  DefaultTableCapacity: number;
  AutoAssignTables: boolean;
  EnableTableTransfer: boolean;
  EnableSplitBill: boolean;
  AllowMultipleSalesPerTable: boolean;
  UseKitchenScreen: boolean;
  TableLayoutJson?: string | null;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt?: string | null;
}

export interface CreateRestaurantScreenDto {
  Name: string;
  BranchId?: string | null;
  QuickSaleScreenId: number;
  DefaultTableCapacity?: number;
  EnableTableTransfer?: boolean;
  EnableSplitBill?: boolean;
}

export interface UpdateRestaurantScreenDto {
  Id: number;
  Name?: string;
  BranchId?: string | null;
  DefaultTableCapacity?: number;
  AutoAssignTables?: boolean;
  EnableTableTransfer?: boolean;
  EnableSplitBill?: boolean;
  AllowMultipleSalesPerTable?: boolean;
  IsActive?: boolean;
}

export interface RestaurantTableGroupDto {
  Id: number;
  RestaurantScreenId: number;
  Name: string;
  DisplayOrder: number;
  CreatedAt: string;
  UpdatedAt?: string | null;
}

export interface CreateRestaurantTableGroupDto {
  RestaurantScreenId: number;
  Name: string;
  DisplayOrder?: number;
}

export interface UpdateRestaurantTableGroupDto {
  Id: number;
  Name?: string;
  DisplayOrder?: number;
}

export interface RestaurantTableDto {
  Id: number;
  RestaurantScreenId: number;
  Name: string;
  Capacity: number;
  TableGroupId?: number | null;
  TableGroupName?: string | null;
  Shape: string;
  Notes?: string | null;
  IsOccupied: boolean;
  DisplayOrder: number;
  CreatedAt: string;
  UpdatedAt?: string | null;
}

export interface CreateRestaurantTableDto {
  RestaurantScreenId: number;
  Name: string;
  Capacity?: number;
  TableGroupId?: number | null;
  Shape?: string;
  Notes?: string | null;
  DisplayOrder?: number;
}

export interface UpdateRestaurantTableDto {
  Id: number;
  Name?: string;
  Capacity?: number;
  TableGroupId?: number | null;
  Shape?: string;
  Notes?: string | null;
  IsOccupied?: boolean;
  DisplayOrder?: number;
}

export interface BulkCreateRestaurantTableDto {
  RestaurantScreenId: number;
  Prefix?: string;
  StartNumber?: number;
  Count: number;
  Capacity?: number;
  TableGroupId?: number | null;
  Shape?: string;
}

// ============================================
// RESTAURANT WAITER TYPES
// ============================================

export interface RestaurantWaiterDto {
  Id: number;
  RestaurantScreenId: number;
  WaiterName: string;
  WaiterPin: string; // ⚠️ Listede gösterilmemeli!
  CanCloseSale: boolean;
  CanCloseTable: boolean;
  CanAddProduct: boolean;
  CanRemoveProduct: boolean;
  CanClearCart: boolean;
  CanPrintToKitchen: boolean;
  CanDeletePrintedItems: boolean;
  CanDeleteUnprintedItems?: boolean; // Yazdırılmamış ürün silme yetkisi
  CanTransferTable: boolean;
  CanPosRefund: boolean;
  CanCancelReceipt?: boolean | null; // Adisyon iptal yetkisi
  CanCancelUnprintedReceipt?: boolean; // Yazdırılmamış adisyon iptal yetkisi
  CanPrintReceipt?: boolean | null; // Adisyon yazdırma yetkisi
  CanAddProductAfterReceiptPrint?: boolean; // Adisyon sonrası ürün ekleme
  CanUsePavoP2P?: boolean;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt?: string | null;
}

export interface CreateRestaurantWaiterDto {
  RestaurantScreenId: number;
  WaiterName: string;
  WaiterPin: string;
  CanCloseSale?: boolean;
  CanCloseTable?: boolean;
  CanAddProduct?: boolean;
  CanRemoveProduct?: boolean;
  CanClearCart?: boolean;
  CanPrintToKitchen?: boolean;
  CanDeletePrintedItems?: boolean;
  CanDeleteUnprintedItems?: boolean; // Yazdırılmamış ürün silme yetkisi
  CanTransferTable?: boolean;
  CanPosRefund?: boolean;
  CanCancelReceipt?: boolean; // Adisyon iptal yetkisi
  CanCancelUnprintedReceipt?: boolean; // Yazdırılmamış adisyon iptal yetkisi
  CanPrintReceipt?: boolean; // Adisyon yazdırma yetkisi
  CanAddProductAfterReceiptPrint?: boolean; // Adisyon sonrası ürün ekleme
  CanUsePavoP2P?: boolean;
  IsActive?: boolean;
}

export interface UpdateRestaurantWaiterDto {
  Id: number;
  WaiterName?: string;
  WaiterPin?: string;
  CanCloseSale?: boolean;
  CanCloseTable?: boolean;
  CanAddProduct?: boolean;
  CanRemoveProduct?: boolean;
  CanClearCart?: boolean;
  CanPrintToKitchen?: boolean;
  CanDeletePrintedItems?: boolean;
  CanDeleteUnprintedItems?: boolean; // Yazdırılmamış ürün silme yetkisi
  CanTransferTable?: boolean;
  CanPosRefund?: boolean;
  CanCancelReceipt?: boolean; // Adisyon iptal yetkisi
  CanCancelUnprintedReceipt?: boolean; // Yazdırılmamış adisyon iptal yetkisi
  CanPrintReceipt?: boolean; // Adisyon yazdırma yetkisi
  CanAddProductAfterReceiptPrint?: boolean; // Adisyon sonrası ürün ekleme
  CanUsePavoP2P?: boolean;
  IsActive?: boolean;
}

export interface ValidateWaiterPinDto {
  RestaurantScreenId: number;
  Pin: string;
}

export interface ValidateWaiterResponseDto {
  IsValid: boolean;
  WaiterId?: number | null;
  WaiterName?: string | null;
  CanCloseSale?: boolean | null;
  CanCloseTable?: boolean | null;
  CanAddProduct?: boolean | null;
  CanRemoveProduct?: boolean | null;
  CanClearCart?: boolean | null;
  CanPrintToKitchen?: boolean | null;
  CanDeletePrintedItems?: boolean | null;
  CanDeleteUnprintedItems?: boolean | null; // Yazdırılmamış ürün silme yetkisi
  CanTransferTable?: boolean | null;
  CanPosRefund?: boolean | null;
  CanCancelReceipt?: boolean | null; // Adisyon iptal yetkisi
  CanCancelUnprintedReceipt?: boolean | null; // Yazdırılmamış adisyon iptal yetkisi
  CanPrintReceipt?: boolean | null; // Adisyon yazdırma yetkisi
  CanAddProductAfterReceiptPrint?: boolean | null; // Adisyon sonrası ürün ekleme
  CanUsePavoP2P?: boolean | null;
  Message?: string | null;
}

// ============================================
// RESTAURANT PAYMENT TYPES
// ============================================

export interface RestaurantPaymentDto {
  paymentMethod: string;
  amount: number;
  paymentPercentage?: number;
  cashboxId?: number;
  cashboxName?: string;
  bankAccountId?: number;
  bankAccountName?: string;
  bankSubAccountId?: number;
  bankSubAccountName?: string;
  authCode?: string;
  batchNo?: string;
  maskedCardNumber?: string;
  cardType?: string;
  fiscalTransactionId?: string;
  accountTransactionId?: number;
  cashboxTransactionId?: number;
  bankTransactionId?: number;
  paymentProviderType?: string;
  paymentProviderId?: number;
  paymentProviderName?: string;
  notes?: string;
  createdBy?: string;
}

export interface RestaurantPaymentResponseDto {
  Id: number;
  CompletedSaleId: number;
  PaymentMethod: string;
  Amount: number;
  PaymentPercentage?: number;
  CashboxId?: number;
  CashboxName?: string;
  BankAccountId?: number;
  BankAccountName?: string;
  BankSubAccountId?: number;
  BankSubAccountName?: string;
  AuthCode?: string;
  BatchNo?: string;
  MaskedCardNumber?: string;
  CardType?: string;
  FiscalTransactionId?: string;
  AccountTransactionId?: number;
  CashboxTransactionId?: number;
  BankTransactionId?: number;
  PaymentProviderType?: string;
  PaymentProviderId?: number;
  PaymentProviderName?: string;
  Notes?: string;
  CreatedAt: string;
  CreatedBy?: string;
}

// ============================================
// RESTAURANT ACTIVE SALES (Açık Masalar)
// ============================================

export interface RestaurantActiveSaleDto {
  Id: number;
  RestaurantScreenId: number;
  BranchId?: string | null; // ✅ YENİ: Şube ID
  TableId: number;
  TableName?: string | null;
  SubTotal: number;
  DiscountAmount: number;
  TaxAmount: number;
  TotalAmount: number;
  OpenedAt: string;
  OpenedBy?: string | null;
  Notes?: string | null;
  WaiterActionsJson?: string | null; // Garson işlemleri JSON
  ReservedLongCode?: string | null; // ✅ Rezerve edilen uzun kod (Fatura için)
  ReservedShortCode?: string | null; // ✅ Rezerve edilen kısa kod (Fiş için)
  KitchenPrintCount?: number; // ✅ YENİ: Mutfağa kaç kez yazdırıldı (0 = ilk, 1+ = ilave)
  LastKitchenPrintAt?: string | null; // ✅ YENİ: Son mutfak yazdırma zamanı
  IsReceiptPrinted?: boolean; // ✅ YENİ: Adisyon yazdırıldı mı
  ReceiptPrintedAt?: string | null; // ✅ YENİ: Adisyon yazdırma zamanı
  ReceiptPrintedBy?: string | null; // ✅ YENİ: Adisyonu yazdıran
  // ✅ SEPET BÖLME ALANLARI
  IsSplitParent?: boolean; // Bu satış bölünmüş mü?
  SplitParentId?: number | null; // Bölünmüş sepetin ana satış ID'si
  SplitIndex?: number; // Bölünme sırası (0 = ana, 1+ = bölünmüş)
  // ✨ Pavo Restoran Ödendi Onay Sistemi
  PavoPaymentPending?: boolean; // true: Pavo'dan ödendi, web onayı bekliyor (KOYU MOR)
  PavoPaymentClosedAt?: string | null; // Pavo ödemesinin alındığı zaman
  PavoPaymentCashierId?: number | null;
  PavoPaymentCashierName?: string | null;
  PavoPaymentCashierType?: string | null; // 'waiter' | 'cashier'
  PavoPaymentAmount?: number | null;
  PavoPaymentSaleNumber?: string | null;
  PavoPaymentMethod?: string | null; // 'cash' | 'pos' | 'partial' | 'open_account' | 'Nakit' | ...
  PavoCompletedSaleId?: number | null;
  ItemCount?: number;
  Items?: RestaurantActiveSaleItemDto[];
  CreatedAt: string;
  UpdatedAt?: string | null;
}

/**
 * ✨ Pavo Restoran Ödendi Onay — confirm-pavo-close endpoint body.
 */
export interface ConfirmPavoCloseDto {
  ConfirmedByCashierName?: string | null;
  ConfirmedByCashierId?: number | null;
}

/**
 * ✨ Pavo Restoran Ödendi Onay — reprint-pavo-receipt response data.
 */
export interface ReprintPavoReceiptData {
  id: number;
  companyId: string;
  restaurantScreenId: number;
  branchId?: string | null;
  tableId: number;
  tableName: string;
  tableGroupName?: string | null;
  saleNumber: string;
  subTotal: number;
  discountAmount: number;
  taxAmount: number;
  totalAmount: number;
  itemsJson: string;
  paymentMethod: string;
  paymentDetailsJson?: string | null;
  openedAt: string;
  openedBy?: string | null;
  openedByName?: string | null;
  completedAt: string;
  completedBy?: string | null;
  completedByName?: string | null;
  durationMinutes?: number;
  notes?: string | null;
  waiterActionsJson?: string | null;
  invoiceNumber?: string | null;
  referenceNumber?: string | null;
  closedByWaiterId?: string | null;
  closedByWaiterName?: string | null;
  reservedShortCode?: string | null;
  isFiscalReceipt?: boolean;
  payments?: Array<{
    id: number;
    paymentMethod: string;
    amount: number;
    paymentPercentage?: number;
    maskedCardNumber?: string | null;
    authCode?: string | null;
    batchNo?: string | null;
    cardType?: string | null;
  }>;
}

export interface CreateRestaurantActiveSaleDto {
  RestaurantScreenId: number;
  BranchId?: string | null; // ✅ YENİ: Şube ID
  TableId: number;
  OpenedBy?: string | null;
  Notes?: string | null;
  OpenedAt?: string; // Türkiye saati - frontend'den gönderilir
  ReservedLongCode?: string | null; // ✅ Rezerve edilen uzun kod
  ReservedShortCode?: string | null; // ✅ Rezerve edilen kısa kod
  // ✅ SEPET BÖLME ALANLARI
  IsSplitParent?: boolean;
  SplitParentId?: number | null;
  SplitIndex?: number;
}

export interface UpdateRestaurantActiveSaleDto {
  SubTotal?: number;
  DiscountAmount?: number;
  TaxAmount?: number;
  TotalAmount?: number;
  Notes?: string | null;
  WaiterActionsJson?: string | null; // ✅ YENİ: Garson işlemleri JSON
  TableId?: number; // ✅ YENİ: Masa taşıma için (TableName otomatik - Navigation Property)
  IsSplitParent?: boolean; // ✅ SEPET BÖLME: Sepet bölündüğünde true yapılır
  KitchenPrintCount?: number; // ✅ YENİ: Mutfak yazdırma sayısı
  LastKitchenPrintAt?: string | null; // ✅ YENİ: Son mutfak yazdırma zamanı
}

export interface RestaurantActiveSaleItemDto {
  Id: number;
  ActiveSaleId: number;
  ProductId: string; // ✅ GUID/UUID string
  ProductName: string;
  Quantity: number;
  UnitPrice: number;
  TotalPrice: number;
  DiscountPercent: number;
  DiscountAmount: number;
  Status: string;
  Notes?: string | null;
  AddedAt: string;
  AddedBy?: string | null;
  PrintedQuantity?: number; // ✅ YENİ: Mutfağa yazdırılan miktar
  LastPrintedAt?: string | null; // ✅ YENİ: Son mutfak yazdırma zamanı
  CreatedAt: string;
  UpdatedAt?: string | null;
}

export interface CreateRestaurantActiveSaleItemDto {
  ActiveSaleId: number;
  ProductId: string; // ✅ GUID/UUID string
  ProductName: string;
  Quantity: number;
  UnitPrice: number;
  DiscountPercent?: number; // Varsayılan: 0
  Notes?: string | null;
  AddedBy?: string | null;
  // ⚠️ TotalPrice, DiscountAmount, Status backend'de otomatik hesaplanıyor
}

// ============================================
// RESTAURANT SCREEN API
// ============================================

export const restaurantScreenApi = {
  // Şirkete ait tüm restoran ekranlarını getir
  getByCompanyId: async (): Promise<RestaurantScreenDto[]> => {
    const response = await restaurantApi.get(`/api/RestaurantScreen`);
    return response.data;
  },

  // ID'ye göre restoran ekranı getir
  getById: async (id: number): Promise<RestaurantScreenDto> => {
    const response = await restaurantApi.get(`/api/RestaurantScreen/${id}`);
    return response.data;
  },

  // QuickSaleScreenId'ye göre restoran ekranı getir
  getByQuickSaleScreenId: async (quickSaleScreenId: number): Promise<RestaurantScreenDto> => {
    const response = await restaurantApi.get(`/api/RestaurantScreen/quicksale/${quickSaleScreenId}`);
    return response.data;
  },

  // Yeni restoran ekranı oluştur
  create: async (data: CreateRestaurantScreenDto): Promise<RestaurantScreenDto> => {
    const response = await restaurantApi.post('/api/RestaurantScreen', data);
    return response.data;
  },

  // Restoran ekranı güncelle
  update: async (id: number, data: UpdateRestaurantScreenDto): Promise<RestaurantScreenDto> => {
    const response = await restaurantApi.post(`/api/RestaurantScreen/update/${id}`, data);
    return response.data;
  },

  // Restoran ekranı sil
  delete: async (id: number): Promise<void> => {
    await restaurantApi.post(`/api/RestaurantScreen/delete/${id}`);
  },

  // Masa düzeni kaydet
  saveLayout: async (id: number, tableLayoutJson: string | null): Promise<{ Message: string; TableLayoutJson: string | null }> => {
    const response = await restaurantApi.post(`/api/RestaurantScreen/save-layout/${id}`, { Id: id, TableLayoutJson: tableLayoutJson });
    return response.data;
  },
};

// ============================================
// RESTAURANT TABLE GROUP API
// ============================================

export const restaurantTableGroupApi = {
  // Restoran ekranına ait tüm masa gruplarını getir
  getByRestaurantScreenId: async (restaurantScreenId: number): Promise<RestaurantTableGroupDto[]> => {
    const response = await restaurantApi.get(`/api/RestaurantTableGroup/screen/${restaurantScreenId}`);
    return response.data;
  },

  // ID'ye göre masa grubu getir
  getById: async (id: number): Promise<RestaurantTableGroupDto> => {
    const response = await restaurantApi.get(`/api/RestaurantTableGroup/${id}`);
    return response.data;
  },

  // Yeni masa grubu oluştur
  create: async (data: CreateRestaurantTableGroupDto): Promise<RestaurantTableGroupDto> => {
    const response = await restaurantApi.post('/api/RestaurantTableGroup', data);
    return response.data;
  },

  // Masa grubu güncelle
  update: async (id: number, data: UpdateRestaurantTableGroupDto): Promise<RestaurantTableGroupDto> => {
    const response = await restaurantApi.post(`/api/RestaurantTableGroup/update/${id}`, data);
    return response.data;
  },

  // Masa grubu sil
  delete: async (id: number): Promise<void> => {
    await restaurantApi.post(`/api/RestaurantTableGroup/delete/${id}`);
  },
};

// ============================================
// RESTAURANT TABLE API
// ============================================

export const restaurantTableApi = {
  // Restoran ekranına ait tüm masaları getir
  getByRestaurantScreenId: async (restaurantScreenId: number): Promise<RestaurantTableDto[]> => {
    const response = await restaurantApi.get(`/api/RestaurantTable/screen/${restaurantScreenId}`);
    return response.data;
  },

  // Masa grubuna ait masaları getir
  getByTableGroupId: async (tableGroupId: number): Promise<RestaurantTableDto[]> => {
    const response = await restaurantApi.get(`/api/RestaurantTable/group/${tableGroupId}`);
    return response.data;
  },

  // ID'ye göre masa getir
  getById: async (id: number): Promise<RestaurantTableDto> => {
    const response = await restaurantApi.get(`/api/RestaurantTable/${id}`);
    return response.data;
  },

  // Yeni masa oluştur
  create: async (data: CreateRestaurantTableDto): Promise<RestaurantTableDto> => {
    const response = await restaurantApi.post('/api/RestaurantTable', data);
    return response.data;
  },

  // Toplu masa oluştur
  bulkCreate: async (data: BulkCreateRestaurantTableDto): Promise<{ Message: string; Count: number }> => {
    const response = await restaurantApi.post('/api/RestaurantTable/bulk', data);
    return response.data;
  },

  // Masa güncelle
  update: async (id: number, data: UpdateRestaurantTableDto): Promise<RestaurantTableDto> => {
    const response = await restaurantApi.post(`/api/RestaurantTable/update/${id}`, data);
    return response.data;
  },

  // Masa durumunu güncelle (Dolu/Boş)
  updateOccupiedStatus: async (id: number, isOccupied: boolean): Promise<{ Message: string; IsOccupied: boolean }> => {
    const response = await restaurantApi.post(`/api/RestaurantTable/${id}/occupied`, isOccupied);
    return response.data;
  },

  // Masa sil
  delete: async (id: number): Promise<void> => {
    await restaurantApi.post(`/api/RestaurantTable/delete/${id}`);
  },
};

// ============================================
// RESTAURANT WAITER API
// ============================================

export const restaurantWaiterApi = {
  // Restoran ekranına ait tüm garsonları getir
  getByRestaurantScreenId: async (restaurantScreenId: number): Promise<RestaurantWaiterDto[]> => {
    const response = await restaurantApi.get(`/api/RestaurantWaiter/screen/${restaurantScreenId}`);
    return response.data;
  },

  // ID'ye göre garson getir
  getById: async (id: number): Promise<RestaurantWaiterDto> => {
    const response = await restaurantApi.get(`/api/RestaurantWaiter/${id}`);
    return response.data;
  },

  // Yeni garson ekle
  create: async (data: CreateRestaurantWaiterDto): Promise<RestaurantWaiterDto> => {
    const response = await restaurantApi.post('/api/RestaurantWaiter', data);
    return response.data;
  },

  // Garson bilgilerini güncelle
  update: async (id: number, data: UpdateRestaurantWaiterDto): Promise<RestaurantWaiterDto> => {
    const response = await restaurantApi.post(`/api/RestaurantWaiter/update/${id}`, data);
    return response.data;
  },

  // Garson sil
  delete: async (id: number): Promise<void> => {
    await restaurantApi.post(`/api/RestaurantWaiter/delete/${id}`);
  },

  // Garson şifre doğrulama (Giriş için)
  validatePin: async (data: ValidateWaiterPinDto): Promise<ValidateWaiterResponseDto> => {
    const response = await restaurantApi.post('/api/RestaurantWaiter/validate-pin', data);
    return response.data;
  },
};

// ============================================
// RESTAURANT ACTIVE SALE API (Açık Masalar)
// ============================================

export const restaurantActiveSaleApi = {
  // Restoran ekranına ait tüm aktif satışları getir
  getByRestaurantScreenId: async (restaurantScreenId: number): Promise<RestaurantActiveSaleDto[]> => {
    const response = await restaurantApi.get(`/api/RestaurantActiveSale/screen/${restaurantScreenId}`);
    return response.data;
  },

  // ✅ BANT GENİŞLİĞİ: Masalar ekranı polling'i için HAFİF özet liste.
  // Items ve WaiterActionsJson gelmez — masa kartlarının ihtiyacı olan tüm
  // skaler alanlar (renk durumu, Pavo bilgileri, OpenedAt, tutarlar) mevcut.
  getScreenSummary: async (restaurantScreenId: number): Promise<RestaurantActiveSaleDto[]> => {
    const response = await restaurantApi.get(`/api/RestaurantActiveSale/screen/${restaurantScreenId}/summary`);
    return response.data;
  },

  // Masa ID'sine göre aktif satış getir
  getByTableId: async (tableId: number): Promise<RestaurantActiveSaleDto | null> => {
    try {
      const response = await restaurantApi.get(`/api/RestaurantActiveSale/table/${tableId}`);
      return response.data;
    } catch (error: any) {
      // 404 = Masa için aktif satış yok (bu normal bir durum)
      if (error.response && error.response.status === 404) {
        return null;
      }
      // Diğer hatalar için throw et
      throw error;
    }
  },

  // ID'ye göre aktif satış getir
  getById: async (id: number): Promise<RestaurantActiveSaleDto> => {
    const response = await restaurantApi.get(`/api/RestaurantActiveSale/${id}`);
    return response.data;
  },

  // Yeni aktif satış oluştur
  create: async (data: CreateRestaurantActiveSaleDto): Promise<RestaurantActiveSaleDto> => {
    const response = await restaurantApi.post('/api/RestaurantActiveSale', data);
    return response.data;
  },

  // Aktif satış güncelle (toplam tutarlar ve WaiterActionsJson)
  update: async (id: number, data: UpdateRestaurantActiveSaleDto): Promise<RestaurantActiveSaleDto> => {
    const response = await restaurantApi.post(`/api/RestaurantActiveSale/update/${id}`, data);
    return response.data;
  },

  // ✅ BANT GENİŞLİĞİ: Tek garson hareketini sunucu tarafında WaiterActionsJson'a ekle.
  // Eski akış (getById + tüm geçmişi geri gönderme) yerine yalnız yeni hareket gider.
  appendWaiterAction: async (
    id: number,
    action: { action: string; waiter: string; time: string; details?: any },
  ): Promise<{ Success: boolean; Count?: number }> => {
    const response = await restaurantApi.post(`/api/RestaurantActiveSale/append-action/${id}`, {
      Action: action.action,
      Waiter: action.waiter,
      Time: action.time,
      Details: action.details ?? null,
    });
    return response.data;
  },

  // Aktif satış sil
  delete: async (id: number): Promise<void> => {
    await restaurantApi.post(`/api/RestaurantActiveSale/delete/${id}`);
  },

  // ✅ YENİ: Adisyon yazdırıldı olarak işaretle
  markReceiptPrinted: async (activeSaleId: number, printedBy: string): Promise<{
    Success: boolean;
    Message: string;
    IsReceiptPrinted?: boolean;
    ReceiptPrintedAt?: string;
    ReceiptPrintedBy?: string;
  }> => {
    try {
      const response = await restaurantApi.post(`/api/RestaurantActiveSale/mark-receipt-printed/${activeSaleId}`, {
        PrintedBy: printedBy
      });
      return response.data;
    } catch (error: any) {
      console.error('[restaurantActiveSaleApi.markReceiptPrinted] Hata:', error);
      return {
        Success: false,
        Message: error?.response?.data?.Message || error?.message || 'Bilinmeyen bir hata oluştu'
      };
    }
  },

  // ✨ Pavo Restoran Ödendi Onay — Web kasiyer "Tamam" tıklayınca masayı kapatır.
  // Aktif satışı (PavoPaymentPending=true olan) DELETE eder, masayı boşaltır.
  confirmPavoClose: async (
    activeSaleId: number,
    body?: ConfirmPavoCloseDto,
  ): Promise<{
    Success: boolean;
    Message: string;
    TableId?: number;
    ActiveSaleId?: number;
    PavoCompletedSaleId?: number | null;
    AlreadyClosed?: boolean;
  }> => {
    try {
      const response = await restaurantApi.post(
        `/api/RestaurantActiveSale/confirm-pavo-close/${activeSaleId}`,
        body ?? {},
      );
      return response.data;
    } catch (error: any) {
      console.error('[restaurantActiveSaleApi.confirmPavoClose] Hata:', error);
      return {
        Success: false,
        Message:
          error?.response?.data?.Message || error?.message || 'Bilinmeyen bir hata oluştu',
      };
    }
  },

  // ✨ Pavo Restoran Ödendi Onay — "Fişi Yeniden Yazdır" için CompletedSale snapshot'ını çeker.
  reprintPavoReceipt: async (
    activeSaleId: number,
  ): Promise<{
    Success: boolean;
    Message: string;
    Data?: ReprintPavoReceiptData;
  }> => {
    try {
      const response = await restaurantApi.post(
        `/api/RestaurantActiveSale/reprint-pavo-receipt/${activeSaleId}`,
      );
      return response.data;
    } catch (error: any) {
      console.error('[restaurantActiveSaleApi.reprintPavoReceipt] Hata:', error);
      return {
        Success: false,
        Message:
          error?.response?.data?.Message || error?.message || 'Bilinmeyen bir hata oluştu',
      };
    }
  },

  // ✅ YENİ: Ödeme sonrası masayı kapat (CompletedSales'e taşı)
  completeSale: async (activeSaleId: number, data: {
    saleNumber?: string;
    subTotal?: number;
    discountAmount?: number;
    taxAmount?: number;
    totalAmount?: number;
    paymentMethod?: string;
    paymentDetailsJson?: string;
    openedByName?: string;
    completedBy?: string;
    completedByName?: string;
    notes?: string;
    invoiceNumber?: string;
    referenceNumber?: string;
    closedByWaiterId?: string;
    closedByWaiterName?: string;
    isFiscalReceipt?: boolean;
    payments?: RestaurantPaymentDto[];
  }): Promise<{
    success: boolean;
    message: string;
    data?: {
      completedSaleId: number;
      saleNumber: string;
      totalAmount: number;
      paymentMethod: string;
      durationMinutes: number;
      itemCount: number;
      invoiceNumber?: string;
      referenceNumber?: string;
    };
  }> => {
    try {
      const response = await restaurantApi.post(`/api/RestaurantActiveSale/complete/${activeSaleId}`, data);
      const apiData = response.data;
      
      // ✅ Backend PascalCase döndürüyor, camelCase'e dönüştür
      return {
        success: apiData.Success ?? apiData.success ?? false,
        message: apiData.Message ?? apiData.message ?? 'Bilinmeyen hata',
        data: apiData.Data ? {
          completedSaleId: apiData.Data.CompletedSaleId ?? apiData.Data.completedSaleId,
          saleNumber: apiData.Data.SaleNumber ?? apiData.Data.saleNumber,
          totalAmount: apiData.Data.TotalAmount ?? apiData.Data.totalAmount,
          paymentMethod: apiData.Data.PaymentMethod ?? apiData.Data.paymentMethod,
          durationMinutes: apiData.Data.DurationMinutes ?? apiData.Data.durationMinutes,
          itemCount: apiData.Data.ItemCount ?? apiData.Data.itemCount,
          invoiceNumber: apiData.Data.InvoiceNumber ?? apiData.Data.invoiceNumber,
          referenceNumber: apiData.Data.ReferenceNumber ?? apiData.Data.referenceNumber,
        } : (apiData.data ? {
          completedSaleId: apiData.data.CompletedSaleId ?? apiData.data.completedSaleId,
          saleNumber: apiData.data.SaleNumber ?? apiData.data.saleNumber,
          totalAmount: apiData.data.TotalAmount ?? apiData.data.totalAmount,
          paymentMethod: apiData.data.PaymentMethod ?? apiData.data.paymentMethod,
          durationMinutes: apiData.data.DurationMinutes ?? apiData.data.durationMinutes,
          itemCount: apiData.data.ItemCount ?? apiData.data.itemCount,
          invoiceNumber: apiData.data.InvoiceNumber ?? apiData.data.invoiceNumber,
          referenceNumber: apiData.data.ReferenceNumber ?? apiData.data.referenceNumber,
        } : undefined)
      };
    } catch (error: any) {
      console.error('[restaurantActiveSaleApi.completeSale] Hata:', error);
      return {
        success: false,
        message: error?.response?.data?.Message || error?.message || 'Bilinmeyen bir hata oluştu'
      };
    }
  },
};

// ============================================
// RESTAURANT COMPLETED SALE (Tamamlanmış Satışlar)
// ============================================

export interface RestaurantCompletedSaleDto {
  Id: number;
  RestaurantScreenId: number;
  BranchId?: string | null; // ✅ YENİ: Şube ID
  TableId: number;
  TableName: string;
  TableGroupName?: string | null;
  SaleNumber: string;
  SubTotal: number;
  DiscountAmount: number;
  TaxAmount: number;
  TotalAmount: number;
  ItemsJson: string;
  PaymentMethod: string;
  PaymentDetailsJson?: string | null;
  OpenedAt: string;
  OpenedBy?: string | null;
  OpenedByName?: string | null;
  CompletedAt: string;
  CompletedBy?: string | null;
  CompletedByName?: string | null;
  DurationMinutes?: number;
  Notes?: string | null;
  WaiterActionsJson?: string | null; // ✅ Garson işlemleri
  InvoiceNumber?: string | null; // ✅ Fatura numarası
  ReferenceNumber?: string | null; // ✅ Referans numarası
  ClosedByWaiterId?: string | null; // ✅ Hesabı kapatan garson ID
  ClosedByWaiterName?: string | null; // ✅ Hesabı kapatan garson adı
  ReservedShortCode?: string | null; // ✅ Fiş/Yazıcı kısa kodu (RS-1, RS-2...)
  IsFiscalReceipt?: boolean;
  CancellationReason?: string | null;
  Payments?: RestaurantPaymentResponseDto[];
}

export interface CreateRestaurantCompletedSaleDto {
  CompanyId?: string;
  RestaurantScreenId: number;
  BranchId?: string | null;
  TableId: number;
  TableName: string;
  TableGroupName?: string | null;
  SaleNumber: string;
  SubTotal: number;
  DiscountAmount: number;
  TaxAmount: number;
  TotalAmount: number;
  ItemsJson: string;
  PaymentMethod: string;
  PaymentDetailsJson?: string | null;
  OpenedAt: string;
  OpenedBy?: string | null;
  OpenedByName?: string | null;
  CompletedBy?: string | null;
  CompletedByName?: string | null;
  Notes?: string | null;
  WaiterActionsJson?: string | null; // ✅ Garson işlemleri
  InvoiceNumber?: string | null; // ✅ Fatura numarası
  ReferenceNumber?: string | null; // ✅ Referans numarası
  CancellationReason?: string | null;
}

// ============================================
// RESTAURANT ACTIVE SALE ITEM API (Sipariş Kalemleri)
// ============================================

export const restaurantActiveSaleItemApi = {
  // Aktif satışa ait tüm ürünleri getir
  getByActiveSaleId: async (activeSaleId: number): Promise<RestaurantActiveSaleItemDto[]> => {
    const response = await restaurantApi.get(`/api/RestaurantActiveSaleItem/sale/${activeSaleId}`);
    return response.data;
  },

  // ID'ye göre ürün getir
  getById: async (id: number): Promise<RestaurantActiveSaleItemDto> => {
    const response = await restaurantApi.get(`/api/RestaurantActiveSaleItem/${id}`);
    return response.data;
  },

  // Yeni ürün ekle
  create: async (data: CreateRestaurantActiveSaleItemDto): Promise<RestaurantActiveSaleItemDto> => {
    const response = await restaurantApi.post('/api/RestaurantActiveSaleItem', data);
    return response.data;
  },

  // Ürün güncelle (miktar, fiyat vb.)
  update: async (id: number, data: { Quantity?: number; TotalPrice?: number; Notes?: string; PrintedQuantity?: number }): Promise<RestaurantActiveSaleItemDto> => {
    const response = await restaurantApi.post(`/api/RestaurantActiveSaleItem/update/${id}`, data);
    return response.data;
  },

  // Ürün sil
  delete: async (id: number): Promise<void> => {
    await restaurantApi.post(`/api/RestaurantActiveSaleItem/delete/${id}`);
  },

  // ✅ YENİ: Mutfağa yazdırılan ürünleri işaretle
  markAsPrinted: async (data: MarkItemsAsPrintedDto): Promise<MarkAsPrintedResponse> => {
    const response = await restaurantApi.post('/api/RestaurantActiveSaleItem/mark-as-printed', data);
    return response.data;
  },
};

// ============================================
// MUTFAK YAZDIRMA — karar sunucuda (gts_restaurant_api)
// İstemci yalnız "yazdır" der; sunucu farkı kilit altında hesaplar, işaretler,
// fiş kaydını yazar ve basılacak listeyi döner. API PascalCase döner.
// ============================================

export interface KitchenPrintTicketLine {
  ItemId: number;
  ProductId: string;
  Name: string;
  /** Bu fişte basılacak adet (satır adedi − daha önce yazdırılan) */
  Quantity: number;
  UnitPrice: number;
  Note?: string | null;
  HasMenu: boolean;
  SelectedOptionsJson?: string | null;
  MenuNormalItemsJson?: string | null;
  TotalPriceDifference: number;
  AddedBy?: string | null;
}

export interface KitchenPrintedTicketLine {
  ItemId: number;
  ProductId: string;
  Name: string;
  PrintedQuantity: number;
  Quantity: number;
}

export interface KitchenPrintPreviewDto {
  ActiveSaleId: number;
  RestaurantScreenId: number;
  TableId: number;
  TableName: string;
  PrintCount: number;
  IsAdditionalOrder: boolean;
  ToPrint: KitchenPrintTicketLine[];
  AlreadyPrinted: KitchenPrintedTicketLine[];
}

export interface KitchenPrintTicket {
  /** Aynı RequestId daha önce işlendi; cevap saklı kayıttan, hiçbir şey değişmedi */
  Duplicate: boolean;
  /** Basılacak fark yoktu */
  NothingToPrint: boolean;
  KitchenPrintId: number;
  ActiveSaleId: number;
  TableId: number;
  TableName: string;
  PrintNo: number;
  IsAdditionalOrder: boolean;
  ShortCode?: string | null;
  LongCode?: string | null;
  PrintedBy?: string | null;
  PrintedAt: string;
  MasaSiparisiId?: number | null;
  Items: KitchenPrintTicketLine[];
  AlreadyPrinted: KitchenPrintedTicketLine[];
}

export interface KitchenPrintRequestDto {
  requestId: string;
  actorName?: string | null;
  actorUid?: string | null;
  qrMenuId?: string | null;
  quickSaleScreenId?: number | null;
  customerAccountName?: string | null;
}

export const restaurantKitchenPrintApi = {
  /** Değişiklik yapmaz: ne basılacak, ne basılmış */
  preview: async (activeSaleId: number): Promise<KitchenPrintPreviewDto> => {
    const response = await restaurantApi.get(`/api/RestaurantActiveSale/${activeSaleId}/kitchen-print/preview`);
    return response.data;
  },
  /** Kilitli karar + işaretleme + fiş kaydı; dönen listeyi istemci basar */
  print: async (activeSaleId: number, request: KitchenPrintRequestDto): Promise<KitchenPrintTicket> => {
    const response = await restaurantApi.post(`/api/RestaurantActiveSale/${activeSaleId}/kitchen-print`, request);
    return response.data;
  },
  /** Son basılan fiş (yeniden basma); adetlere dokunmaz. Yoksa null. */
  last: async (activeSaleId: number): Promise<KitchenPrintTicket | null> => {
    try {
      const response = await restaurantApi.get(`/api/RestaurantActiveSale/${activeSaleId}/kitchen-print/last`);
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  },
};

// ✅ YENİ: Mutfak yazdırma için DTO'lar
export interface MarkItemsAsPrintedDto {
  ActiveSaleId: number;
  PrintedItems: PrintedItemInfo[];
}

export interface PrintedItemInfo {
  ItemId: number;
  PrintedQuantity: number;
}

export interface MarkAsPrintedResponse {
  Success: boolean;
  Message: string;
  KitchenPrintCount: number;
  LastKitchenPrintAt: string;
}

// ============================================
// RESTAURANT COMPLETED SALE API (Tamamlanmış Satışlar)
// ============================================

export const restaurantCompletedSaleApi = {
  // Şirkete ait tamamlanmış satışları getir
  getByCompanyId: async (startDate?: string, endDate?: string, branchId?: string | null, eodDiagId?: string | null): Promise<RestaurantCompletedSaleDto[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (branchId) params.append('branchId', branchId);
    if (eodDiagId) params.append('eodDiagId', eodDiagId);
    const response = await restaurantApi.get(`/api/RestaurantCompletedSale?${params.toString()}`);
    return response.data;
  },

  // Restoran ekranına ait tamamlanmış satışları getir
  getByRestaurantScreenId: async (restaurantScreenId: number, startDate?: string, endDate?: string, eodDiagId?: string | null): Promise<RestaurantCompletedSaleDto[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (eodDiagId) params.append('eodDiagId', eodDiagId);
    const response = await restaurantApi.get(`/api/RestaurantCompletedSale/screen/${restaurantScreenId}?${params.toString()}`);
    return response.data;
  },

  // ID'ye göre tamamlanmış satış getir
  getById: async (id: number): Promise<RestaurantCompletedSaleDto> => {
    const response = await restaurantApi.get(`/api/RestaurantCompletedSale/${id}`);
    return response.data;
  },

  // Yeni tamamlanmış satış oluştur (Masa kapatma)
  create: async (data: CreateRestaurantCompletedSaleDto): Promise<RestaurantCompletedSaleDto> => {
    const response = await restaurantApi.post('/api/RestaurantCompletedSale', data);
    return response.data;
  },
};

// ============================================
// END OF DAY REPORT TYPES
// ============================================

export interface EndOfDayReportDto {
  Id: number;
  ScreenId?: number | null;
  ScreenType: string;
  StartDate: string;
  EndDate: string;
  Balance: number;
  CashTotal: number;
  PosTotal: number;
  OtherTotal: number;
  CancelledTotal: number;
  TransactionCount: number;
  ClosedBy: string;
  ClosedByName?: string | null;
  ClosedByEmail?: string | null;
  Notes?: string | null;
  CreatedAt: string;
  BranchId?: string | null;
  ScreensJson?: string | null;
}

export interface CreateEndOfDayReportDto {
  ScreenId?: number | null;
  ScreenType: string;
  StartDate: string;
  EndDate: string;
  Balance: number;
  CashTotal: number;
  PosTotal: number;
  OtherTotal: number;
  CancelledTotal: number;
  TransactionCount: number;
  ClosedBy: string;
  ClosedByName?: string | null;
  ClosedByEmail?: string | null;
  Notes?: string | null;
  BranchId?: string | null;
  ScreensJson?: string | null;
}

export interface EndOfDayReportListResponse {
  Data: EndOfDayReportDto[];
  TotalCount: number;
}

export interface CheckExistingReportResponse {
  Exists: boolean;
  ExistingReportId?: number | null;
  ExistingReportDate?: string | null;
}

// ============================================
// END OF DAY REPORT API
// ============================================

export const endOfDayReportApi = {
  // Yeni gün sonu raporu oluştur
  create: async (data: CreateEndOfDayReportDto): Promise<{ Message: string; Data: EndOfDayReportDto }> => {
    const response = await restaurantApi.post('/api/EndOfDayReport', data);
    return response.data;
  },

  // Şirkete ait gün sonu raporlarını listele
  getByCompanyId: async (startDate?: string, endDate?: string): Promise<EndOfDayReportListResponse> => {
    let url = `/api/EndOfDayReport`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;
    
    const response = await restaurantApi.get(url);
    return response.data;
  },

  // Belirli bir gün sonu raporunu getir
  getById: async (id: number): Promise<EndOfDayReportDto> => {
    const response = await restaurantApi.get(`/api/EndOfDayReport/${id}`);
    return response.data;
  },

  // Zaten rapor var mı kontrol et
  checkExisting: async (
    screenId: number | null,
    screenType: string,
    startDate: string,
    endDate: string
  ): Promise<CheckExistingReportResponse> => {
    const params = new URLSearchParams();
    if (screenId !== null) params.append('screenId', screenId.toString());
    params.append('screenType', screenType);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    
    const response = await restaurantApi.get(`/api/EndOfDayReport/check-existing?${params.toString()}`);
    return response.data;
  },

  // Şirkete ait en son gün sonu raporunu getir
  getLatest: async (): Promise<{ Success: boolean; Data: EndOfDayReportDto | null }> => {
    const response = await restaurantApi.get(`/api/EndOfDayReport/latest`);
    return response.data;
  },

  // Gün sonu raporunu sil
  delete: async (id: number): Promise<{ Message: string }> => {
    const response = await restaurantApi.delete(`/api/EndOfDayReport/${id}`);
    return response.data;
  },
};

// ============================================
// RESTAURANT SPLIT CART API (Sepet Bölme)
// ============================================

export interface SplitTableDto {
  TableId: number;
  OpenedBy?: string | null;
  ReservedLongCode?: string | null;
  ReservedShortCode?: string | null;
}

export interface MoveItemDto {
  ItemId: number;
  FromSaleId: number;
  ToSaleId: number;
  Quantity?: number; // null = tümünü taşı
  MovedBy?: string | null;
}

export interface SplitCartsResponse {
  Success: boolean;
  Data: RestaurantActiveSaleDto[];
  MainSaleId: number;
  SplitCount: number;
}

export interface SplitTableResponse {
  Success: boolean;
  Message: string;
  Data: RestaurantActiveSaleDto;
  MainSaleId: number;
  SplitSaleId: number;
  SplitIndex: number;
}

export interface MoveItemResponse {
  Success: boolean;
  Message: string;
  FromSaleId: number;
  ToSaleId: number;
  MovedQuantity: number;
  FromItems?: Array<{ Id: number; ProductId: string; ProductName: string; Quantity: number; UnitPrice: number; TotalPrice: number; PrintedQuantity: number; ActiveSaleId: number }>;
  ToItems?: Array<{ Id: number; ProductId: string; ProductName: string; Quantity: number; UnitPrice: number; TotalPrice: number; PrintedQuantity: number; ActiveSaleId: number }>;
}

export interface MergeCartsResponse {
  Success: boolean;
  Message: string;
  MainSaleId: number;
  MergedSaleId?: number;
  MergedCount?: number;
  RemainingSplitCount?: number;
}

export const restaurantSplitApi = {
  // Masanın tüm sepetlerini getir (ana + bölünmüş)
  // ✅ MULTI-TENANT: companyId parametresi ile güvenli sorgu
  getByTableId: async (tableId: number): Promise<SplitCartsResponse> => {
    const response = await restaurantApi.get(`/api/RestaurantSplit/by-table/${tableId}`);
    return response.data;
  },

  // Sepet böl - Yeni bölünmüş sepet oluştur
  // ✅ MULTI-TENANT: data.CompanyId ile güvenli işlem
  splitTable: async (data: SplitTableDto): Promise<SplitTableResponse> => {
    const response = await restaurantApi.post('/api/RestaurantSplit/split-table', data);
    return response.data;
  },

  // Ürün taşı - Bir sepetten diğerine
  // ✅ MULTI-TENANT: data.CompanyId ile güvenli işlem
  moveItem: async (data: MoveItemDto): Promise<MoveItemResponse> => {
    const response = await restaurantApi.post('/api/RestaurantSplit/move-item', data);
    return response.data;
  },

  // Sepetleri birleştir - Bölünmüş sepeti ana sepete geri taşı
  // ✅ MULTI-TENANT: companyId parametresi ile güvenli işlem
  merge: async (splitSaleId: number): Promise<MergeCartsResponse> => {
    const response = await restaurantApi.post(`/api/RestaurantSplit/merge/${splitSaleId}`);
    return response.data;
  },

  // Tüm bölünmüş sepetleri birleştir
  // ✅ MULTI-TENANT: companyId parametresi ile güvenli işlem
  mergeAll: async (tableId: number): Promise<MergeCartsResponse> => {
    const response = await restaurantApi.post(`/api/RestaurantSplit/merge-all/${tableId}`);
    return response.data;
  },
};

export default {
  restaurantScreenApi,
  restaurantTableGroupApi,
  restaurantTableApi,
  restaurantWaiterApi,
  restaurantActiveSaleApi,
  restaurantActiveSaleItemApi,
  restaurantCompletedSaleApi,
  endOfDayReportApi, // ✅ Gün Sonu Raporu API
  restaurantSplitApi, // ✅ Sepet Bölme API
};

